import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationHeader,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";
import { api } from "@/modules/api";
import { toastModule } from "@/modules/toast";
import {
  MDSButton,
  MDSButtonVariant,
  MDSText,
  MDSTextField,
  MDSTextFieldVariant,
  MDSTextVariant,
} from "@/mds";
import { MDSTextColor } from "@/mds/components/text";
import { useAppStore } from "@/store";
import styled from "@emotion/styled";
import { FC, FormEvent, useState } from "react";

const MIN_PASSWORD_LENGTH = 8;

export const SettingsDebugAccountPage: FC = () => {
  const { store } = useAppStore();
  const accountEmail = store.account.myAccount.profileEmailAddress;
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const canSubmit =
    password.length >= MIN_PASSWORD_LENGTH &&
    confirmPassword.length >= MIN_PASSWORD_LENGTH &&
    password === confirmPassword &&
    !isSaving;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setErrorMessage("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords must match.");
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    const response = await api.post("/v2/auth/email-password/current-account", {
      body: { password },
    });
    setIsSaving(false);

    if (response.error) {
      setErrorMessage("Could not set the password for this account.");
      return;
    }

    setPassword("");
    setConfirmPassword("");
    toastModule.triggerToast({ content: "Password set for email login" });
  };

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader breadcrumb="Debug" title="Debug account" />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <StyledForm onSubmit={handleSubmit}>
            <StyledTextGroup>
              <MDSText variant={MDSTextVariant.ParagraphS}>Email/password login</MDSText>
              <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
                {accountEmail}
              </MDSText>
            </StyledTextGroup>
            <StyledFieldGroup>
              <MDSTextField
                variant={MDSTextFieldVariant.Form}
                type="password"
                placeholder="Password"
                aria-label="Password"
                value={password}
                isDisabled={isSaving}
                onChange={value => {
                  setPassword(value);
                  setErrorMessage(null);
                }}
              />
              <MDSTextField
                variant={MDSTextFieldVariant.Form}
                type="password"
                placeholder="Confirm password"
                aria-label="Confirm password"
                value={confirmPassword}
                isDisabled={isSaving}
                onChange={value => {
                  setConfirmPassword(value);
                  setErrorMessage(null);
                }}
              />
            </StyledFieldGroup>
            {errorMessage && (
              <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Danger}>
                {errorMessage}
              </MDSText>
            )}
            <MDSButton
              variant={MDSButtonVariant.Contrast}
              type="submit"
              isDisabled={!canSubmit}
              isLoading={isSaving}
            >
              Set password
            </MDSButton>
          </StyledForm>
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[4]};
  max-width: 360px;
`;

const StyledTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;

  > div {
    width: 100%;
  }
`;
